import type { ComponentConfig } from "@puckeditor/core"
import { SectionBase, sectionStyle } from "../lib/section"
import { getResolvers, SiteProduct } from "../lib/resolvers"
import { ProductTile } from "../lib/product-tile"

export type ProductListProps = SectionBase & {
  title: string
  /** Comma separated product handles picked by the author, order preserved. */
  productHandles: string
  columns: 2 | 3
  ctaLabel: string
  /**
   * Resolved by the storefront renderer through the registered resolvers
   * (resolveData below). The editor shows placeholders.
   */
  _products?: SiteProduct[]
}

/**
 * Curated product list for blog posts: the author names specific product
 * handles instead of a collection query, so an article can point at the
 * exact items it reviews.
 */
export const ProductList: ComponentConfig<ProductListProps> = {
  fields: {
    title: { type: "textarea", contentEditable: true },
    productHandles: { type: "text", label: "Product handles (comma separated)", contentEditable: true },
    ctaLabel: { type: "text", label: "Tile button label", contentEditable: true },
    columns: {
      type: "radio",
      options: [
        { value: 2, label: "2 columns" },
        { value: 3, label: "3 columns" },
      ],
    },
    background: {
      type: "radio",
      options: [
        { value: "none", label: "Transparent" },
        { value: "muted", label: "Muted" },
        { value: "accent", label: "Dark" },
      ],
    },
    paddingY: {
      type: "radio",
      options: [
        { value: "none", label: "None" },
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
  },

  resolveData: async ({ props }) => {
    const resolvers = getResolvers()
    if (!resolvers) {
      // Admin editor or unregistered host: keep placeholders.
      return { props: {} }
    }
    const handles = props.productHandles
      .split(",")
      .map((h: string) => h.trim())
      .filter(Boolean)
    const products = await Promise.all(handles.map((h) => resolvers.productByHandle(h)))
    return { props: { _products: products.filter((p): p is SiteProduct => p !== null) } }
  },

  defaultProps: {
    title: "In this article",
    productHandles: "",
    columns: 3,
    ctaLabel: "View product",
    background: "muted",
    paddingY: "md",
  },

  render: ({ title, _products, columns, background, paddingY }) => {
    const s = sectionStyle({ background, paddingY })
    const products = _products ?? []

    return (
      <section style={s}>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 24px" }}>
          {title && <h2 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: 24, lineHeight: 1.3 }}>{title}</h2>}
          {products.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 24 }}>
              {products.map((product) => (
                <ProductTile key={product.id} product={product} ctaLabel="View" showDescription={false} />
              ))}
            </div>
          ) : (
            <div
              style={{
                padding: "48px 24px",
                border: "1px dashed #d1d5db",
                borderRadius: 8,
                textAlign: "center",
                color: "#9ca3af",
                fontSize: 15,
              }}
            >
              Add product handles to show a curated list.
            </div>
          )}
        </div>
      </section>
    )
  },
}
